/**
 * Header Quick Links Enhancement Script
 * Add this to your theme's main JavaScript file or as a separate asset
 */

(function() {
  'use strict';
  
  // Configuration
  const CONFIG = {
    selectors: {
      wrapper: '.header-quick-links-wrapper',
      item: '.quick-link-item',
      banner: '.quick-link-banner',
      button: '.quick-link-button',
      bannerButton: '.banner-button',
      image: '.quick-link-image img',
      bannerImage: '.banner-image img'
    },
    classes: {
      loaded: 'quick-links-loaded',
      animated: 'quick-links-animated',
      visible: 'quick-links-visible'
    },
    animations: {
      enabled: true,
      staggerDelay: 100, // ms between each item animation
      intersectionThreshold: 0.1
    },
    analytics: {
      enabled: true,
      category: 'Quick Links'
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuickLinks);
  } else {
    initQuickLinks();
  }

  function initQuickLinks() {
    const wrapper = document.querySelector(CONFIG.selectors.wrapper);
    if (!wrapper) return;

    console.log('🚀 Initializing Header Quick Links Enhancement');

    // Initialize all features
    setupIntersectionObserver();
    setupHoverEffects();
    setupClickTracking();
    setupKeyboardNavigation();
    setupLazyLoading();
    setupResponsiveFeatures();
    
    // Mark as loaded
    wrapper.classList.add(CONFIG.classes.loaded);
  }

  /**
   * Intersection Observer for scroll animations
   */
  function setupIntersectionObserver() {
    if (!CONFIG.animations.enabled || !window.IntersectionObserver) return;

    const items = document.querySelectorAll(CONFIG.selectors.item + ', ' + CONFIG.selectors.banner);
    if (items.length === 0) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          // Staggered animation delay
          setTimeout(() => {
            entry.target.classList.add(CONFIG.classes.visible);
          }, index * CONFIG.animations.staggerDelay);
          
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: CONFIG.animations.intersectionThreshold,
      rootMargin: '50px'
    });

    items.forEach(item => {
      // Add initial animation class
      item.style.opacity = '0';
      item.style.transform = 'translateY(30px)';
      item.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
      
      observer.observe(item);
    });

    // CSS for visible state
    addStyles(`
      .${CONFIG.classes.visible} {
        opacity: 1 !important;
        transform: translateY(0) !important;
      }
    `);
  }

  /**
   * Enhanced hover effects
   */
  function setupHoverEffects() {
    const items = document.querySelectorAll(CONFIG.selectors.item + ', ' + CONFIG.selectors.banner);
    
    items.forEach(item => {
      const image = item.querySelector(CONFIG.selectors.image) || item.querySelector(CONFIG.selectors.bannerImage);
      
      item.addEventListener('mouseenter', () => {
        // Enhanced hover animation
        item.style.transform = 'translateY(-8px) scale(1.02)';
        item.style.boxShadow = '0 20px 40px rgba(0,0,0,0.15)';
        
        // Image zoom effect
        if (image) {
          image.style.transform = 'scale(1.1)';
        }
        
        // Button glow effect
        const button = item.querySelector(CONFIG.selectors.button) || item.querySelector(CONFIG.selectors.bannerButton);
        if (button) {
          button.style.boxShadow = '0 0 20px rgba(0,0,0,0.3)';
        }
      });
      
      item.addEventListener('mouseleave', () => {
        // Reset animations
        item.style.transform = '';
        item.style.boxShadow = '';
        
        if (image) {
          image.style.transform = '';
        }
        
        const button = item.querySelector(CONFIG.selectors.button) || item.querySelector(CONFIG.selectors.bannerButton);
        if (button) {
          button.style.boxShadow = '';
        }
      });
    });
  }

  /**
   * Click tracking and analytics
   */
  function setupClickTracking() {
    if (!CONFIG.analytics.enabled) return;

    const trackableElements = document.querySelectorAll(
      CONFIG.selectors.item + ' a, ' + 
      CONFIG.selectors.banner + ' a, ' +
      CONFIG.selectors.button + ', ' +
      CONFIG.selectors.bannerButton
    );

    trackableElements.forEach(element => {
      element.addEventListener('click', (e) => {
        const item = element.closest(CONFIG.selectors.item) || element.closest(CONFIG.selectors.banner);
        const title = item.querySelector('.quick-link-title, .banner-title')?.textContent || 'Unknown';
        const url = element.href || element.closest('a')?.href || 'No URL';
        
        // Google Analytics 4
        if (typeof gtag !== 'undefined') {
          gtag('event', 'quick_link_click', {
            event_category: CONFIG.analytics.category,
            event_label: title,
            value: url
          });
        }
        
        // Facebook Pixel
        if (typeof fbq !== 'undefined') {
          fbq('track', 'ViewContent', {
            content_name: title,
            content_category: CONFIG.analytics.category
          });
        }
        
        // Console log for debugging
        console.log('📊 Quick Link Click:', { title, url });
        
        // Visual feedback
        showClickFeedback(element);
      });
    });
  }

  /**
   * Visual click feedback
   */
  function showClickFeedback(element) {
    const originalTransform = element.style.transform;
    element.style.transform = 'scale(0.95)';
    element.style.transition = 'transform 0.1s ease';
    
    setTimeout(() => {
      element.style.transform = originalTransform;
    }, 100);
  }

  /**
   * Keyboard navigation support
   */
  function setupKeyboardNavigation() {
    const focusableElements = document.querySelectorAll(
      CONFIG.selectors.item + ' a, ' + 
      CONFIG.selectors.banner + ' a'
    );

    focusableElements.forEach(element => {
      element.addEventListener('focus', () => {
        const item = element.closest(CONFIG.selectors.item) || element.closest(CONFIG.selectors.banner);
        item.style.outline = '3px solid #007acc';
        item.style.outlineOffset = '2px';
      });
      
      element.addEventListener('blur', () => {
        const item = element.closest(CONFIG.selectors.item) || element.closest(CONFIG.selectors.banner);
        item.style.outline = '';
        item.style.outlineOffset = '';
      });
      
      element.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          showClickFeedback(element);
          setTimeout(() => {
            element.click();
          }, 150);
        }
      });
    });
  }

  /**
   * Enhanced lazy loading for images
   */
  function setupLazyLoading() {
    const images = document.querySelectorAll(CONFIG.selectors.image + ', ' + CONFIG.selectors.bannerImage);
    
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            
            // Add loading class
            img.classList.add('quick-link-loading');
            
            // Create new image to preload
            const newImg = new Image();
            newImg.onload = () => {
              img.src = newImg.src;
              img.classList.remove('quick-link-loading');
              img.classList.add('quick-link-loaded');
            };
            
            newImg.onerror = () => {
              img.classList.remove('quick-link-loading');
              img.classList.add('quick-link-error');
            };
            
            newImg.src = img.dataset.src || img.src;
            imageObserver.unobserve(img);
          }
        });
      });

      images.forEach(img => imageObserver.observe(img));
    }

    // Add CSS for loading states
    addStyles(`
      .quick-link-loading {
        opacity: 0.5;
        filter: blur(2px);
      }
      
      .quick-link-loaded {
        opacity: 1;
        filter: none;
        transition: opacity 0.3s ease, filter 0.3s ease;
      }
      
      .quick-link-error {
        opacity: 0.3;
        background: #f0f0f0;
      }
    `);
  }

  /**
   * Responsive features and touch enhancements
   */
  function setupResponsiveFeatures() {
    let touchStartY = 0;
    let touchStartTime = 0;
    
    const items = document.querySelectorAll(CONFIG.selectors.item + ', ' + CONFIG.selectors.banner);
    
    items.forEach(item => {
      // Touch events for mobile
      item.addEventListener('touchstart', (e) => {
        touchStartY = e.touches[0].clientY;
        touchStartTime = Date.now();
        
        // Add touch feedback
        item.style.transform = 'scale(0.98)';
        item.style.transition = 'transform 0.1s ease';
      }, { passive: true });
      
      item.addEventListener('touchend', (e) => {
        const touchEndY = e.changedTouches[0].clientY;
        const touchDuration = Date.now() - touchStartTime;
        const touchDistance = Math.abs(touchEndY - touchStartY);
        
        // Reset transform
        item.style.transform = '';
        
        // If it was a tap (not a scroll), add click effect
        if (touchDuration < 200 && touchDistance < 10) {
          showClickFeedback(item);
        }
      }, { passive: true });
      
      item.addEventListener('touchcancel', () => {
        item.style.transform = '';
      }, { passive: true });
    });

    // Viewport width changes
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateResponsiveLayout();
      }, 250);
    });
  }

  /**
   * Update layout on resize
   */
  function updateResponsiveLayout() {
    const wrapper = document.querySelector(CONFIG.selectors.wrapper);
    if (!wrapper) return;
    
    const grid = wrapper.querySelector('.quick-links-grid');
    if (!grid) return;
    
    const viewportWidth = window.innerWidth;
    
    // Force single column on very small screens
    if (viewportWidth < 480) {
      grid.style.gridTemplateColumns = '1fr';
    } else if (viewportWidth < 768) {
      grid.style.gridTemplateColumns = 'repeat(auto-fit, minmax(250px, 1fr))';
    } else {
      grid.style.gridTemplateColumns = '';
    }
  }

  /**
   * Utility function to add CSS styles
   */
  function addStyles(css) {
    const styleSheet = document.createElement('style');
    styleSheet.type = 'text/css';
    styleSheet.innerText = css;
    document.head.appendChild(styleSheet);
  }

  /**
   * Performance monitoring
   */
  function measurePerformance() {
    if ('performance' in window && performance.mark) {
      performance.mark('quick-links-start');
      
      // Measure after initialization
      requestAnimationFrame(() => {
        performance.mark('quick-links-end');
        performance.measure('quick-links-init', 'quick-links-start', 'quick-links-end');
        
        const measure = performance.getEntriesByName('quick-links-init')[0];
        if (measure) {
          console.log(`⚡ Quick Links initialized in ${measure.duration.toFixed(2)}ms`);
        }
      });
    }
  }

  /**
   * Error handling and fallbacks
   */
  window.addEventListener('error', (e) => {
    if (e.filename && e.filename.includes('quick-links')) {
      console.warn('⚠️ Quick Links Enhancement Error:', e.message);
      
      // Fallback: ensure basic functionality works
      const wrapper = document.querySelector(CONFIG.selectors.wrapper);
      if (wrapper) {
        wrapper.classList.add('quick-links-fallback');
      }
    }
  });

  // Public API for external access
  window.QuickLinksEnhancement = {
    config: CONFIG,
    reinitialize: initQuickLinks,
    measurePerformance: measurePerformance
  };

  // Initialize performance monitoring
  measurePerformance();

})();